import { httpServices } from '@/api/client';
import { getAuth0UserId } from '@/services/auth0UserStore';

/** The claims of a JWT, unverified (the API verifies); null if it is not one. */
export const decodeJwtClaims = (
  token: string | null | undefined,
): Record<string, unknown> | null => {
  if (!token) return null;
  const part = token.split('|')[0].split('.')[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(
      decodeURIComponent(
        Array.from(
          json,
          c => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`,
        ).join(''),
      ),
    );
    return claims && typeof claims === 'object' ? claims : null;
  } catch {
    return null;
  }
};

/** The token the API client sends (Auth0 in memory, or the stored SaaS/dev token). */
export const currentToken = (): string =>
  httpServices.llmService.currentToken();

/** Expiry of the current token in ms since the epoch, or null when unknown. */
export const currentTokenExpiry = (): number | null => {
  const exp = decodeJwtClaims(currentToken())?.exp;
  return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null;
};

/**
 * A stable key for the signed-in user, for per-user client caches: the token
 * subject (the user id for dev and SaaS tokens, the Auth0 user id for Auth0),
 * then the e-mail address. Null when nobody is signed in.
 */
/** The per-user key (as currentUserKey) of a token's subject, or null. */
export const userKeyOfToken = (token: string | null | undefined) => {
  const claims = decodeJwtClaims(token);
  const subject = claims && (claims.sub ?? claims.user_id ?? claims.id);
  return subject ? `u:${String(subject)}` : null;
};

export const currentUserKey = (fallback?: {
  email?: string | null;
  username?: string | null;
}): string | null => {
  const claims = decodeJwtClaims(currentToken());
  const subject =
    getAuth0UserId() ||
    (claims && (claims.sub ?? claims.user_id ?? claims.id)) ||
    fallback?.email ||
    fallback?.username;
  return subject ? `u:${String(subject)}` : null;
};
